import { Badge, Button, Card, Hero, Section } from '../components/ui';
import { Upload, Sparkles, UserCheck, CheckCircle, Download, ArrowRight, Zap } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Upload,
    title: 'Upload Documents',
    description: 'Upload invoices, purchase orders, receipts, or any business document via drag-and-drop, email forwarding, or API integration. Support for PDF, images, and scanned documents.',
    features: ['Batch upload support', 'Email-to-upload', 'API integration', 'Multi-format support']
  },
  {
    number: '02',
    icon: Sparkles,
    title: 'AI Extraction',
    description: 'Our LLM-powered engine automatically extracts key data points from your documents with high accuracy. Field-level confidence scores help identify areas that may need review.',
    features: ['99%+ accuracy', 'Multi-language support', 'Custom field extraction', 'Confidence scoring']
  },
  {
    number: '03',
    icon: UserCheck,
    title: 'Human Review',
    description: 'Low-confidence fields are flagged for human review. Your team can quickly validate and correct extractions in an intuitive interface, training the AI over time.',
    features: ['Smart queue prioritization', 'One-click corrections', 'Continuous learning', 'Role-based workflows']
  },
  {
    number: '04',
    icon: CheckCircle,
    title: 'Validation & Enrichment',
    description: 'Extracted data is validated against your master data (vendors, GL codes, cost centers). Automatic matching and duplicate detection ensure data quality.',
    features: ['Master data matching', 'Duplicate detection', 'Business rules engine', 'Exception handling']
  },
  {
    number: '05',
    icon: Download,
    title: 'Export to ERP',
    description: 'Clean, validated data is automatically exported to your ERP system in the required format. Real-time sync keeps your systems up-to-date.',
    features: ['Direct ERP integration', 'Custom export formats', 'Webhook notifications', 'Audit trail']
  }
];

const benefits = [
  {
    icon: Zap,
    title: '10x Faster Processing',
    description: 'Process documents in seconds instead of hours. Automated extraction eliminates manual data entry.'
  },
  {
    icon: CheckCircle,
    title: '99%+ Accuracy',
    description: 'AI-powered extraction with human oversight ensures the highest data quality standards.'
  },
  {
    icon: ArrowRight,
    title: 'Seamless Integration',
    description: 'Direct integration with your existing ERP and business systems. No workflow disruption.'
  }
];

const useCases = [
  {
    title: 'Accounts Payable',
    description: 'Automate invoice processing from receipt to approval to payment.',
    time: '5 min → 30 sec'
  },
  {
    title: 'Procurement',
    description: 'Extract and validate purchase orders and delivery notes automatically.',
    time: '10 min → 1 min'
  },
  {
    title: 'Expense Management',
    description: 'Process employee expense receipts and reimbursements in real-time.',
    time: '15 min → 2 min'
  },
  {
    title: 'Contract Management',
    description: 'Extract key terms, dates, and obligations from contracts and agreements.',
    time: '30 min → 3 min'
  }
];

export const HowItWorks = () => {
  return (
    <>
      <Hero
        title="How textadapt Works"
        description="From document upload to ERP integration in 5 simple steps. Automate your document processing workflow with AI-powered extraction."
        size="sm"
      />

      <Section centered>
        <div className="how-it-works-container">
          <div className="how-it-works-intro">
            <Badge variant="primary" appearance="soft">
              Simple Process
            </Badge>
            <h2>Five Steps to Automated Processing</h2>
            <p>
              Our intelligent workflow combines AI automation with human oversight to deliver accurate results every
              time.
            </p>
          </div>

          <div className="how-it-works-grid">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={step.number} elevation="lg" className="how-it-works-step">
                  <div className="how-it-works-step__main">
                    <div className="how-it-works-step__icon" aria-hidden>
                      <Icon size={32} strokeWidth={1.75} />
                      <span className="how-it-works-step__index">{step.number}</span>
                    </div>

                    <div className="how-it-works-step__content">
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                      <div className="how-it-works-step__features">
                        {step.features.map((feature) => (
                          <Badge key={feature} variant="neutral" appearance="soft">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  {index < steps.length - 1 && (
                    <div className="how-it-works-step__divider" aria-hidden>
                      <ArrowRight size={24} strokeWidth={1.75} />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </Section>

      <Section title="Why It Works" subtitle="Built for speed, accuracy, and integration" background="subtle" centered>
        <div className="features-advanced-grid how-it-works-benefits">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <Card key={benefit.title} elevation="md" className="features-advanced-card">
                <div className="features-advanced-card__icon">
                  <Icon size={24} />
                </div>
                <div className="features-advanced-card__body">
                  <h4>{benefit.title}</h4>
                  <p>{benefit.description}</p>
                </div>
              </Card>
            );
          })}
        </div>
      </Section>

      <Section title="Real-World Impact" subtitle="See the time savings across different use cases" centered>
        <div className="how-it-works-impact">
          <div className="how-it-works-impact-grid">
            {useCases.map((useCase) => (
              <Card key={useCase.title} elevation="sm" className="how-it-works-impact-card">
                <h4>{useCase.title}</h4>
                <p>{useCase.description}</p>
                <span className="how-it-works-impact-card__time">{useCase.time}</span>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      <Section centered>
        <div className="pricing-cta">
          <h2>Ready to automate your workflow?</h2>
          <p>Process your first 100 documents free. No credit card required.</p>
          <div>
            <Button variant="primary" size="lg" as="a" href="/contact">
              Get Started
            </Button>
            <Button variant="outline" size="lg" as="a" href="/pricing">
              View Pricing
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};
